import * as Dialog from '@radix-ui/react-dialog';
import ClearIcon from '../icons/ClearIcon';
import api from '../utils/api';
import { useEffect, useState, forwardRef } from 'react';
import LoadingIcon from '../icons/LoadingIcon';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Box } from '@mui/material';

interface FullScreenVideoProps {
  id?: string;
  url?: string;
}

const FullScreenVideo = forwardRef<HTMLDivElement, FullScreenVideoProps>(({ id, url: propUrl }, ref) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(propUrl || null);
  const [isLoading, setIsLoading] = useState(!propUrl);

  useEffect(() => {
    // If URL is provided directly, use it
    if (propUrl) {
      setVideoUrl(propUrl);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch by ID
    if (!id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // Try to fetch from videos endpoint first, fallback to images endpoint
    api({
      url: `/image-generation/v1/videos/${id}`,
      responseType: 'json'
    })
      .then((res) => {
        setVideoUrl(res.data.url || res.data.videoUrl);
        setIsLoading(false);
      })
      .catch(() => {
        // Fallback to images endpoint if videos endpoint fails
        api({
          url: `/image-generation/v1/images/${id}`,
          responseType: 'json'
        })
          .then((res) => {
            setVideoUrl(res.data.url);
            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
          });
      });
  }, [id, propUrl]);

  return (
    <Dialog.Overlay 
      className="fixed inset-0 bg-[#29292999] bg-opacity-60 backdrop-blur-[1.5px] z-[3000] flex overflow-y-auto py-10"
    >
      <Dialog.Content 
        aria-describedby="" 
        className="relative m-auto px-10 z-[3100]" 
        ref={ref}
      >
        <VisuallyHidden>
          <Dialog.Title>Fullscreen Video</Dialog.Title>
        </VisuallyHidden>
        <Dialog.Close asChild>
          <button
            type="button"
            className="absolute right-4 top-4 z-[5000] cursor-pointer hover:opacity-80 transition-opacity"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: '50%',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
            }}
            aria-label="Close video"
          >
            <ClearIcon style={{ width: '24px', height: '24px', color: 'white', display: 'block' }} />
          </button>
        </Dialog.Close>
        {videoUrl && !isLoading ? (
          <Box
            sx={{
              position: 'relative',
              width: 'auto',
              maxWidth: '100%',
              height: 'auto',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => {
              // Prevent clicks on video container from closing the dialog
              e.stopPropagation();
            }}
          >
            <Box
              component="video"
              src={videoUrl}
              autoPlay
              controls
              playsInline
              sx={{
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '20px',
                display: 'block',
              }}
            />
          </Box>
        ) : (
          isLoading && <LoadingIcon className="w-48 text-[#FEFE17] m-auto" />
        )}
      </Dialog.Content>
    </Dialog.Overlay>
  );
});

// Set display name for easier debugging
FullScreenVideo.displayName = 'FullScreenVideo';
export default FullScreenVideo;

